import {
  Card,
  Divider,
  Flex,
  Form,
  Image,
  Input,
  message,
  Space,
  Typography,
} from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SignInForm } from "../../Components/AuthForms";
import TransitionWrapper from "../../Components/TransitionWrapper";
import PageMetadata from "../../Components/PageMetaData/PageMetaData";
import { getPageMetaData } from "../../Utils/meta-data";
import { useAppContext } from "../../Context/AppContext/AppContext";
import { getGradientStyle } from "../../Utils/generic-functions";
import AuthPageSection from "../../Components/AuthPageSection/AuthPageSection";
import AuthPageFooter from "../../Components/AuthPageFooter/AuthPageFooter";
import organizationService from "../../Services/organization.service";
import apiClient from "../../Services/api-client.service";
import "./index.scss";
import AuthService from "../../Services/auth.service";
import { openNotification } from "../../Components/Notification/Index";

const SignIn = () => {
  const { organization, appInitialized } = useAppContext();
  const [isLoading, setLoading] = useState(false);
  const [isCheckingSSO, setIsCheckingSSO] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect_url = searchParams.get("redirect_url") || null;
  const access_token = searchParams.get("access_token");
  const authService = AuthService.getInstance();

  const handleSignUpClick = () => {
    const signUpUrl = redirect_url
      ? `/sign-up?redirect_url=${redirect_url}`
      : "/sign-up";
    navigate(signUpUrl);
  };

  const handleForgotPasswordClick = () => {
    navigate("/forgot-password");
  };

  // Handle SSO callback with access_token
  useEffect(() => {
    const handleSSOCallback = async () => {
      if (access_token) {
        try {
          setIsCheckingSSO(true);
          // Save token and fetch user data
          authService.setAccessTokenToLocalStorage(access_token);
          await authService.fetchAndStoreCurrentUser();
          
          // Remove access_token from URL
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete("access_token");
          const newSearch = newSearchParams.toString();
          const newUrl = newSearch 
            ? `${window.location.pathname}?${newSearch}` 
            : window.location.pathname;
          window.history.replaceState({}, "", newUrl);
          
          // Show success notification
          openNotification("success", "Welcome back! ", "You've been signed in successfully.");
          
          // Redirect to intended destination or home (validate to prevent open redirect)
          if (redirect_url && redirect_url.startsWith("/") && !redirect_url.startsWith("//")) {
            navigate(redirect_url);
          } else {
            navigate("/");
          }
        } catch (error) {
          console.error("Error handling SSO callback:", error);
          openNotification("error", "Authentication Error", "Failed to complete sign-in. Please try again.");
        } finally {
          setIsCheckingSSO(false);
        }
      }
    };

    handleSSOCallback();
  }, [access_token, authService, navigate, redirect_url, searchParams]);

  // Check for SSO configuration and redirect if needed
  useEffect(() => {
    const checkAndRedirectSSO = async () => {
      // Wait for app to initialize
      if (!appInitialized) {
        return;
      }

      // If we have access_token, don't redirect (let the callback handler process it)
      if (access_token) {
        setIsCheckingSSO(false);
        return;
      }

      try {
        setIsCheckingSSO(true);
        
        // Get organization data if not already loaded
        let orgData = organization;
        if (!orgData) {
          orgData = await organizationService.getOrganizationData();
        }

        // Check if SSO is configured
        if (orgData?.oauthProviderUrl) {
          // Build SSO redirect URL
          const ssoBaseUrl = `${process.env.REACT_APP_API_BASE_URL}/auth/customer-oauth`;
          const redirectUri = encodeURIComponent(
            redirect_url 
              ? `${window.location.origin}/sign-in?redirect_url=${encodeURIComponent(redirect_url)}`
              : `${window.location.origin}/sign-in`
          );
          
          // Generate a simple state token (optional, but good practice)
          const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
          
          // Add format=json to get the OAuth URL as JSON instead of redirecting
          const ssoUrl = `${ssoBaseUrl}?redirect_uri=${redirectUri}&state=${state}&format=json`;
          
          try {
            // Use apiClient to make the request and get JSON response
            const response = await apiClient.get(ssoUrl);
            
            // Extract OAuth URL from JSON response
            const oauthUrl = response.data?.oauthUrl;
            
            if (oauthUrl) {
              // Redirect user to the OAuth provider URL using window.location
              // This ensures the browser URL changes properly
              window.location.href = oauthUrl;
              return;
            } else {
              console.error("No OAuth URL found in SSO response");
              // Fall through to show normal sign-in form
            }
          } catch (error: any) {
            console.error("Error initiating SSO:", error);
            // Fall through to show normal sign-in form
          }
        }
      } catch (error) {
        console.error("Error checking SSO configuration:", error);
        // Fall through to show normal sign-in form
      } finally {
        setIsCheckingSSO(false);
      }
    };

    checkAndRedirectSSO();
  }, [organization, appInitialized, access_token, redirect_url]);

  const handleSuccess = () => {
    if (redirect_url && redirect_url.startsWith("/") && !redirect_url.startsWith("//")) {
      navigate(redirect_url);
    } else {
      navigate("/");
    }
  };

  const meta = organization
    ? getPageMetaData("signin", { organization })
    : getPageMetaData("signin");

  const bg = getGradientStyle(organization?.theme?.primaryColor || "#F7A500");

  const orgLogoUrl = organization?.logo
    ? `https://hiring-assistant-files.s3.ap-south-1.amazonaws.com/organization/${organization?._id}/${organization?.logo}`
    : "/images/qualifyme_light.png";

  // Show loading state while checking SSO or processing callback
  if (isCheckingSSO) {
    return (
      <>
        <PageMetadata title={meta.title} description={meta.description} />
        <TransitionWrapper transitionType="slide-up">
          <section className="signin-page">
            <div className="signin-page-content">
              <div className="left-section">
                <div className="signin-content" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                  <Typography.Text>Redirecting to sign in...</Typography.Text>
                </div>
              </div>
            </div>
          </section>
        </TransitionWrapper>
      </>
    );
  }

  return (
    <>
      <PageMetadata title={meta.title} description={meta.description} />
      <TransitionWrapper transitionType="slide-up">
        <section className="signin-page">
          <div className="signin-page-content">
            <div className="left-section">
              <div className="signin-content">
                <Card
                  className="signin-card"
                  style={{ maxWidth: 400, width: "100%" }}
                  bordered={false}
                  title={
                    <section className="signin-card-title">
                      <Image
                        preview={false}
                        src={orgLogoUrl}
                        fallback={"/images/qualifyme_light.png"}
                        width="48px"
                        height="auto"
                      />
                      <Typography.Title level={4} style={{ margin: 0 }}>
                        Sign in
                      </Typography.Title>
                    </section>
                  }
                >
                  <SignInForm
                    onSuccess={handleSuccess}
                    onForgotPassword={handleForgotPasswordClick}
                    onSignUpClick={handleSignUpClick}
                    redirectUrl={redirect_url}
                  />
                </Card>
                <AuthPageFooter type="signin" />
              </div>
            </div>
            <div className="right-section" style={bg}>
              <AuthPageSection type="signin" />
            </div>
          </div>
        </section>
      </TransitionWrapper>
    </>
  );
};

export default SignIn;
